// AI Order Timeline Modal — big popup that runs a Gemini analysis over
// EVERYTHING linked to an order (order fields, customer 360 snapshot,
// related calls, related quotes, supplier docs).
//
// UX rules (from spec):
//   • Button is ORANGE by default → BRIGHT GREEN once analysis is saved.
//   • Click → we hit `POST /api/orders/{id}/ai-timeline` (takes ~5-20s).
//   • Result is cached on the order doc so re-open is instant.
//
// Consumers use two exports:
//     <AITimelineButton orderId={id} />    ← the button
//     <AITimelineModal orderId={id} onClose={...} />  ← the popup
import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Sparkles, Loader2, X, CheckCircle2, ArrowUpRight,
  TrendingUp, PhoneCall, FileText, AlertTriangle, ListChecks, Clock,
} from "lucide-react";
import { API, token, fmtDateTime, fmtDate, money } from "./_shared";

// ─── Button (state: idle-orange | running | done-green) ─────────────────
export function AITimelineButton({ orderId, size = "sm", onOpen }) {
  const [state, setState] = useState("idle");    // idle | running | done
  const [running, setRunning] = useState(false);

  // On mount, ask the server if we already have a cached summary.  If we
  // do, boot the button in "done" (green) state.
  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/orders/${orderId}/ai-timeline`, {
      headers: { Authorization: `Bearer ${token()}` },
      validateStatus: () => true,
    }).then((r) => {
      if (cancel) return;
      if (r.status === 200) setState("done");
    });
    return () => { cancel = true; };
  }, [orderId]);

  const run = async () => {
    if (running) return;
    setRunning(true); setState("running");
    try {
      await axios.post(`${API}/orders/${orderId}/ai-timeline`, {}, {
        headers: { Authorization: `Bearer ${token()}` },
        timeout: 120000,
      });
      setState("done");
      toast.success("AI Order Timeline ready");
      onOpen?.();     // auto-open the modal so the user sees the result
    } catch (e) {
      setState("idle");
      toast.error("AI analysis failed", { description: e?.response?.data?.detail || e.message });
    } finally { setRunning(false); }
  };

  const cls = size === "sm"
    ? "px-2 py-1 text-[10px]"
    : "px-3 py-1.5 text-[11px]";
  const tone =
    state === "done"    ? "border-emerald-400/60 bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30"
  : state === "running" ? "border-amber-400/60 bg-amber-500/25 text-amber-100 animate-pulse"
                        : "border-orange-500/60 bg-orange-500/15 text-orange-200 hover:bg-orange-500/25";

  return (
    <button
      onClick={state === "done" ? onOpen : run}
      disabled={running}
      className={`inline-flex items-center gap-1.5 rounded border font-mono uppercase tracking-widest ${cls} ${tone}`}
      data-testid={`ai-timeline-btn-${orderId}`}
      title={
        state === "done"    ? "Open AI Order Timeline"
      : state === "running" ? "Running Gemini analysis…"
                            : "Run AI Order Timeline (uses Gemini credits)"
      }
    >
      {running
        ? <Loader2 className="w-3 h-3 animate-spin" />
        : state === "done"
          ? <CheckCircle2 className="w-3 h-3" />
          : <Sparkles className="w-3 h-3" />
      }
      {state === "done" ? "AI 360" : state === "running" ? "Running…" : "AI 360"}
    </button>
  );
}

// ─── The big popup ──────────────────────────────────────────────────────
export function AITimelineModal({ orderId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/orders/${orderId}/ai-timeline`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error("Nothing to show yet", { description: "Run the AI Timeline first." });
      onClose();
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  const rerun = async () => {
    setRerunning(true);
    try {
      const r = await axios.post(`${API}/orders/${orderId}/ai-timeline`, {}, {
        headers: { Authorization: `Bearer ${token()}` }, timeout: 120000,
      });
      setData(r.data);
      toast.success("Re-analysed");
    } catch (e) {
      toast.error("Rerun failed", { description: e?.response?.data?.detail || "" });
    } finally { setRerunning(false); }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center">
        <div className="text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading AI analysis…</div>
      </div>
    );
  }
  if (!data) return null;
  const s = data.summary || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-md grid place-items-center p-4" onClick={onClose} data-testid="ai-timeline-modal">
      <div className="w-full max-w-6xl max-h-[92vh] overflow-hidden rounded-lg border border-emerald-500/40 bg-[#0F1420] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-white/10 bg-gradient-to-r from-emerald-500/10 to-orange-500/10">
          <div>
            <div className="text-[10px] font-mono text-emerald-300 uppercase tracking-widest flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> AI Order Timeline · 360°
            </div>
            <h2 className="text-lg font-semibold text-white mt-0.5 font-mono">Order #{orderId}</h2>
            <div className="text-[10.5px] font-mono text-slate-500 mt-0.5">
              Generated {fmtDateTime(data.generated_at)} · by {data.generated_by || "—"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={rerun} disabled={rerunning}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-white/10 hover:bg-white/[0.06] text-slate-300 font-mono uppercase tracking-widest text-[10px]"
              data-testid="ai-timeline-rerun"
            >
              {rerunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Rerun
            </button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-white/[0.06] text-slate-400 hover:text-white" data-testid="ai-timeline-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto space-y-4">
          {s.parse_error && (
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-200 p-3 text-[12px]">
              ⚠ The model didn't return clean JSON.  Showing the raw text below.
            </div>
          )}

          {/* Summary */}
          {s.summary && (
            <Section title="Summary" icon={Sparkles} tone="text-emerald-300">
              <p className="text-[13px] text-slate-100 leading-relaxed">{s.summary}</p>
            </Section>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Speed metrics */}
            {s.speed_metrics && (
              <Section title="Speed metrics" icon={Clock} tone="text-sky-300">
                <MetricList metrics={{
                  "Order → Confirmed":   s.speed_metrics.order_to_confirmed_hours != null ? `${s.speed_metrics.order_to_confirmed_hours}h` : "—",
                  "Order → Dispatched":  s.speed_metrics.order_to_dispatched_days != null ? `${s.speed_metrics.order_to_dispatched_days}d` : "—",
                  "Quote → Order":       s.speed_metrics.quote_to_order_days != null ? `${s.speed_metrics.quote_to_order_days}d` : "—",
                }} />
              </Section>
            )}

            {/* Margin */}
            {s.margin_analysis && (
              <Section title="Margin analysis" icon={TrendingUp} tone="text-amber-300">
                <MetricList metrics={{
                  "Margin %": s.margin_analysis.margin_percent != null ? `${s.margin_analysis.margin_percent}%` : "—",
                  "Profit":   s.margin_analysis.profit_amount   != null ? money(s.margin_analysis.profit_amount) : "—",
                }} />
                {s.margin_analysis.commentary && (
                  <p className="text-[11.5px] text-slate-400 mt-2 leading-relaxed italic">{s.margin_analysis.commentary}</p>
                )}
              </Section>
            )}

            {/* Conversion signals */}
            {s.conversion_signals && (
              <Section title="Conversion signals" icon={PhoneCall} tone="text-fuchsia-300">
                <MetricList metrics={{
                  "Prior quotes": s.conversion_signals.prior_quotes ?? "—",
                  "Prior calls":  s.conversion_signals.prior_calls ?? "—",
                  "Sentiment":    s.conversion_signals.sentiment_trend || "—",
                }} />
                {s.conversion_signals.commentary && (
                  <p className="text-[11.5px] text-slate-400 mt-2 leading-relaxed italic">{s.conversion_signals.commentary}</p>
                )}
              </Section>
            )}

            {/* Red flags */}
            {Array.isArray(s.red_flags) && s.red_flags.length > 0 && (
              <Section title="Red flags" icon={AlertTriangle} tone="text-rose-300">
                <ul className="space-y-1 text-[12px] text-slate-200">
                  {s.red_flags.map((flag, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="text-rose-300 mt-0.5">•</span>{flag}
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </div>

          {/* Timeline */}
          {Array.isArray(s.timeline_events) && s.timeline_events.length > 0 && (
            <Section title="Timeline" icon={ArrowUpRight} tone="text-teal-300">
              <ol className="space-y-2 border-l border-white/10 pl-4">
                {s.timeline_events.map((ev, i) => (
                  <li key={i} className="relative">
                    <span className="absolute -left-[19px] top-1.5 w-2 h-2 rounded-full bg-teal-400" />
                    <div className="text-[10.5px] font-mono text-slate-500 uppercase tracking-widest">
                      {fmtDate(ev.ts)} · {ev.kind}
                    </div>
                    <div className="text-[13px] text-slate-100">{ev.label}</div>
                  </li>
                ))}
              </ol>
            </Section>
          )}

          {/* Action items */}
          {Array.isArray(s.action_items) && s.action_items.length > 0 && (
            <Section title="Action items" icon={ListChecks} tone="text-amber-300">
              <ul className="space-y-1 text-[13px] text-slate-100">
                {s.action_items.map((it, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-amber-300 mt-0.5">☐</span>{it}
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, tone, children }) {
  return (
    <section className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
      <div className={`text-[10px] font-mono uppercase tracking-widest mb-2 flex items-center gap-1.5 ${tone}`}>
        <Icon className="w-3 h-3" /> {title}
      </div>
      {children}
    </section>
  );
}

function MetricList({ metrics }) {
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
      {Object.entries(metrics).map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-slate-500 text-[10.5px] font-mono uppercase tracking-widest">{k}</dt>
          <dd className="text-slate-100 tabular-nums text-right">{v}</dd>
        </div>
      ))}
    </dl>
  );
}
